import ClearIcon from "@mui/icons-material/Clear";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useGo } from "@refinedev/core";
import { useAutocomplete } from "@refinedev/mui";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";

import UploadField from "@/components/forms/UploadField";
import { CryptorFormData } from "@/pages/cryptor/utils/cryptor.schema";
import { getTranslation } from "@/utils/translation";

type ArtefactName =
  | "artefactLinkWithThePlace"
  | "artefactDescription"
  | "artefactQuote";

interface CryptorArtefactFieldProps {
  cryptorId?: number;
  artefactName: ArtefactName;
  title?: string;
}

const CryptorArtefactField: FC<CryptorArtefactFieldProps> = ({
  cryptorId,
  artefactName,
  title,
}) => {
  const go = useGo();
  const { control, register, setError, watch, setValue } =
    useFormContext<CryptorFormData>();

  const { autocompleteProps } = useAutocomplete({
    resource: "chat-message",
    filters: cryptorId
      ? [{ field: "cryptorId", operator: "eq", value: cryptorId }]
      : undefined,
    defaultValue: undefined,
    pagination: { pageSize: 1000, mode: "server" },
    queryOptions: { enabled: cryptorId !== undefined },
  });

  watch(artefactName);
  const isActive = !!watch(artefactName);

  return (
    <Box>
      <Box display="flex" flexDirection="row" gap={3}>
        <h2>{title}</h2>
        <Controller
          control={control}
          name={artefactName}
          render={({ field }) => {
            if (field.value) {
              return (
                <IconButton
                  onClick={() => {
                    setValue(artefactName, null);
                  }}
                  sx={{ alignSelf: "center" }}
                >
                  <ClearIcon />
                </IconButton>
              );
            }
            return (
              <Button
                onClick={() => {
                  setValue(artefactName, {
                    id: undefined,
                    chatMessageId: 0,
                    image: { url: "" },
                    titlePopUp: "",
                    textPopUp: "",
                    reward: 0,
                  });
                }}
                sx={{ alignSelf: "center" }}
              >
                Ajouter
              </Button>
            );
          }}
        />
      </Box>

      {isActive && (
        <>
          <TextField
            {...register(`${artefactName}.titlePopUp`, {
              required: "Ce champ est requis",
            })}
            defaultValue={null}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Titre de la pop-up"
          />
          <TextField
            {...register(`${artefactName}.textPopUp`, {
              required: "Ce champ est requis",
            })}
            defaultValue={null}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Texte de la pop-up"
            multiline
            minRows={3}
          />

          <Box display="flex" flexDirection="row" gap={3}>
            <TextField
              {...register(`${artefactName}.reward`, {
                required: "Ce champ est requis",
                valueAsNumber: true,
              })}
              defaultValue={null}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="number"
              label="Récompense"
            />

            {cryptorId && (
              <Controller
                control={control}
                name={`${artefactName}.chatMessageId`}
                defaultValue={0}
                render={({ field }) => {
                  return (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={2}
                      minWidth={200}
                      maxWidth={300}
                    >
                      <FormControl margin="normal" style={{ flex: 1 }}>
                        <InputLabel id="children-select-label">
                          Chat message
                        </InputLabel>
                        <Select
                          {...field}
                          defaultValue={field.value}
                          labelId="children-select-label"
                          label="Chat message"
                        >
                          {[
                            { id: null, text: "Aucun" },
                            ...autocompleteProps.options,
                          ].map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              <Stack flex={1} maxWidth={800}>
                                {option.id}{" "}
                                {getTranslation(option.textTranslation)?.value}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        onClick={() => {
                          go({
                            to: `/cryptors/${cryptorId}/chat-message/${field.value}`,
                          });
                        }}
                        disabled={!field.value}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Stack>
                  );
                }}
              />
            )}
          </Box>

          <UploadField
            name={`${artefactName}.image.url`}
            uploadButtonLabel="Upload image"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
        </>
      )}
    </Box>
  );
};

export default CryptorArtefactField;
